'use client';

import type { MessageEvent as SSEMessageEvent } from 'event-source-polyfill';
import { EventSourcePolyfill } from 'event-source-polyfill';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';
import {
  matchSseEvent,
  SseEnvelopeSchema,
  type SseEventDef,
  type SseSubscribe,
  type SseSubscribeReconnect,
} from '@terra/shared-sse-contracts';

type SSEProviderProps = {
  children: React.ReactNode;
};

type SSEContextType = {
  tenantId: string | null;
  subscribe: SseSubscribe;
  subscribeReconnect: SseSubscribeReconnect;
};

const SSEContext = createContext<SSEContextType | null>(null);

const RESUME_DEBOUNCE_MS = 2_000;

export const SSEProvider: React.FC<SSEProviderProps> = ({ children }) => {
  const [tenantId, setTenantId] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const listenersRef = useRef(new Set<(event: SSEMessageEvent) => void>());
  const reconnectListenersRef = useRef(new Set<() => void>());
  const hasOpenedOnceRef = useRef(false);
  const authBlockedRef = useRef(false);
  const lastResumeAtRef = useRef(0);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  const subscribe = useCallback(
    <T,>(
      eventDef: SseEventDef<T>,
      callback: (data: T) => void,
      options?: { filter?: (event: T) => boolean },
    ) => {
      const listener = (event: SSEMessageEvent) => {
        try {
          const parsedEnvelope = SseEnvelopeSchema.safeParse(
            JSON.parse(event.data),
          );
          if (!parsedEnvelope.success) return;

          const innerData = parsedEnvelope.data.data;
          const matched = matchSseEvent(innerData, eventDef);
          if (!matched) return;
          if (options?.filter && !options.filter(matched)) return;
          callback(matched);
        } catch (error) {
          console.error('Error parsing SSE data:', error);
        }
      };
      listenersRef.current.add(listener);
      return () => listenersRef.current.delete(listener);
    },
    [],
  );

  const subscribeReconnect = useCallback<SseSubscribeReconnect>((callback) => {
    reconnectListenersRef.current.add(callback);
    return () => {
      reconnectListenersRef.current.delete(callback);
    };
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      if (!window.electronAPI?.auth) {
        console.warn('SSE Provider - Electron auth API not available yet');
        setIsAuthenticated(false);
        setTenantId(null);
        setIsCheckingAuth(false);
        return;
      }

      try {
        const authResult = await window.electronAPI.auth.isAuthenticated();
        setIsAuthenticated(authResult.authenticated);

        if (authResult.authenticated) {
          const tenantResult =
            await window.electronAPI.auth.getCurrentTenantId();
          if (tenantResult.success && tenantResult.tenantId) {
            setTenantId(tenantResult.tenantId);
          }
        } else {
          setTenantId(null);
        }
      } catch (error) {
        console.error('SSE Provider - Auth check failed:', error);
        setIsAuthenticated(false);
        setTenantId(null);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    void initAuth();

    const cleanupSuccess = window.electronAPI?.auth?.onAuthSuccess(async () => {
      setIsAuthenticated(true);
      const tenantResult = await window.electronAPI?.auth?.getCurrentTenantId();
      if (tenantResult?.success && tenantResult.tenantId) {
        setTenantId(tenantResult.tenantId);
      }
    });

    const cleanupLogout = window.electronAPI?.session?.onLoggedOut(() => {
      setIsAuthenticated(false);
      setTenantId(null);
    });

    return () => {
      cleanupSuccess?.();
      cleanupLogout?.();
    };
  }, []);

  const canOpenSse = !isCheckingAuth && isAuthenticated && Boolean(tenantId);

  useEffect(() => {
    if (!canOpenSse) {
      return;
    }

    let timeoutId: number | undefined = undefined;
    const deferredTimers = new Set<number>();

    const disconnect = () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };

    const connect = () => {
      disconnect();

      try {
        console.log('Initializing SSE connection for tenant:', tenantId);
        const gatewayUrl = getCurrentGatewayUrl();
        const sseUrl = `${gatewayUrl}/sse`;
        console.log('SSE connecting to:', sseUrl);

        const eventSource = new EventSourcePolyfill(sseUrl, {
          withCredentials: true,
          headers: {},
        });

        eventSource.onopen = () => {
          console.log('SSE connection opened for tenant:', tenantId);
          authBlockedRef.current = false;
          if (hasOpenedOnceRef.current) {
            for (const listener of reconnectListenersRef.current) {
              listener();
            }
          } else {
            hasOpenedOnceRef.current = true;
          }
        };

        eventSource.onmessage = (event: SSEMessageEvent) => {
          for (const listener of listenersRef.current) {
            listener(event);
          }
        };

        eventSource.onerror = (event) => {
          console.error('SSE Error:', event);

          const errorEvent = event as {
            status?: number;
            statusText?: string;
          };
          if (errorEvent.status === 401 || errorEvent.status === 403) {
            console.warn(
              `SSE connection failed with ${errorEvent.status} ${errorEvent.statusText}. Waiting for system resume to retry.`,
            );
            authBlockedRef.current = true;
            disconnect();
            return;
          }

          disconnect();
          timeoutId = window.setTimeout(connect, 5000);
        };

        eventSourceRef.current = eventSource;
      } catch (error) {
        console.error('Failed to initialize SSE:', error);
      }
    };

    const forceReconnect = (source: string) => {
      const now = Date.now();
      if (now - lastResumeAtRef.current <= RESUME_DEBOUNCE_MS) {
        return;
      }
      lastResumeAtRef.current = now;
      console.log(`[SSE] Reconnecting (source: ${source})`);
      authBlockedRef.current = false;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = undefined;
      }
      connect();
    };

    const cleanupResumed = window.electronAPI?.system?.onSystemResumed(() => {
      forceReconnect('powerMonitor');
    });

    const cleanupSuspending = window.electronAPI?.system?.onSystemSuspending(
      () => {
        console.log('[SSE] System suspending, closing EventSource');
        if (timeoutId) {
          window.clearTimeout(timeoutId);
          timeoutId = undefined;
        }
        disconnect();
      },
    );

    const deferReconnect = (source: string) => {
      const id = window.setTimeout(() => {
        deferredTimers.delete(id);
        forceReconnect(source);
      }, RESUME_DEBOUNCE_MS);
      deferredTimers.add(id);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        deferReconnect('visibilitychange');
      }
    };

    const handleOnline = () => {
      deferReconnect('online');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    void connect();

    return () => {
      disconnect();
      hasOpenedOnceRef.current = false;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      for (const id of deferredTimers) {
        window.clearTimeout(id);
      }
      deferredTimers.clear();
      cleanupResumed?.();
      cleanupSuspending?.();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [canOpenSse, tenantId]);

  return (
    <SSEContext.Provider value={{ subscribe, subscribeReconnect, tenantId }}>
      {children}
    </SSEContext.Provider>
  );
};

export const useSSE = () => {
  const context = useContext(SSEContext);

  if (!context) {
    throw new Error('useSSE must be used within an SSEProvider');
  }

  return {
    tenantId: context.tenantId,
    subscribe: context.subscribe,
    subscribeReconnect: context.subscribeReconnect,
  };
};
